import { useEffect, useState } from 'react';
import { getOrganizationByCommerce, type CoreOrganization } from '@/lib/zifekCore';
import EntityTimelinePanel from '@/components/feature/EntityTimelinePanel';
import EntityNotesPanel from '@/components/feature/EntityNotesPanel';

interface Props {
  /** Id du marchand (users.id) — sert à résoudre l'organisation du socle. */
  commerceId: number | null | undefined;
  entityType: string;
  entityId: string | number;
  actorName?: string | null;
  actorUserId?: number | null;
  defaultTab?: 'timeline' | 'notes';
}

/**
 * Regroupe la Timeline (spec 31) et les Notes internes (spec 32) du socle,
 * directement exploitables dans n'importe quelle fiche (client, commande,
 * réservation…). Résout l'organisation une seule fois pour les deux panneaux.
 */
export default function EntityActivityTabs({
  commerceId,
  entityType,
  entityId,
  actorName,
  actorUserId,
  defaultTab = 'timeline',
}: Props) {
  const [org, setOrg] = useState<CoreOrganization | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'timeline' | 'notes'>(defaultTab);

  useEffect(() => {
    let cancelled = false;
    if (!commerceId) {
      setOrg(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    getOrganizationByCommerce(commerceId)
      .then((o) => {
        if (!cancelled) setOrg(o);
      })
      .catch(() => {
        if (!cancelled) setOrg(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [commerceId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 rounded-lg border border-background-200/70 bg-background-50">
        <i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i>
      </div>
    );
  }

  if (!org) {
    return (
      <div className="rounded-lg border border-dashed border-background-300/60 bg-background-50 px-4 py-6 text-center">
        <i className="ri-flashlight-line text-2xl text-foreground-300"></i>
        <p className="text-sm text-foreground-500 mt-2">Socle central non initialisé pour ce commerce.</p>
        <p className="text-xs text-foreground-400 mt-1">La Timeline et les Notes du socle s&apos;activent dès que l&apos;organisation Core existe.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
        {([
          { key: 'timeline', label: 'Timeline', icon: 'ri-history-line' },
          { key: 'notes', label: 'Notes', icon: 'ri-sticky-note-line' },
        ] as const).map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
              tab === t.key ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            <i className={t.icon}></i>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'timeline' ? (
        <EntityTimelinePanel
          organizationId={org.id}
          tenantId={org.tenant_id}
          entityType={entityType}
          entityId={entityId}
          actorName={actorName}
          actorUserId={actorUserId}
        />
      ) : (
        <EntityNotesPanel
          organizationId={org.id}
          tenantId={org.tenant_id}
          entityType={entityType}
          entityId={entityId}
          actorName={actorName}
          actorUserId={actorUserId}
        />
      )}
    </div>
  );
}